/* eslint-disable @nx/enforce-module-boundaries */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { APPSTATE_FEATURE_KEY, AppState } from './appstate.reducer';
import {
  Entitlement,
  EntitlementLob,
  GetBrNavigationsResponse,
  Menu,
  WecomeContent,
} from './appstate.models';
import { ProfileType } from '@cigna/cigna-for-brokers/shared/data-access';

const APPSTATE_PROFILE_KEY = 'auth';
// Lookup the 'Appstate' feature state managed by NgRx
const getAppstateState = createFeatureSelector<AppState>(APPSTATE_FEATURE_KEY);
const getProfileState = createFeatureSelector<{
  data: ProfileType;
}>(APPSTATE_PROFILE_KEY);
export const getDetailsLoaded = createSelector(
  getAppstateState,
  (state) => state.detailsLoaded,
);
export const getDetailsError = createSelector(
  getAppstateState,
  (state) => state.detailsError,
);
export const getlobBeforeLogin = createSelector(
  getAppstateState,
  (state) => state.lobBeforeLogin,
);
export const partyRole = createSelector(
  getAppstateState,
  (state) => state.brNavigation?.partyRoles,
);
export const agencyName = createSelector(
  getAppstateState,
  (state) => state.brNavigation?.agencyNames,
);

export const getAdditionalDetailCode = createSelector(
  getAppstateState,
  (state) => state.additionalDetailCode,
);
export const getIsBroker = createSelector(
  getAppstateState,
  (state) => state.isBroker,
);
const getNavigation = createSelector(
  getAppstateState,
  getIsBroker,
  (state, isBroker) => (isBroker ? state.brNavigation : state.navigation),
);
const getSsoId = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.ssoId,
);
const getSsoIdEnc = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.ssoIdEnc,
);

const getFirstName = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.firstName,
);

const getLastName = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.lastName,
);
const getEmail = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.email,
);
const getIsNpnPilot = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.isNpnPilot,
);
const getPhoneNumber = createSelector(
  getAppstateState,
  getNavigation,
  (state, navigation) => navigation?.mobile,
);

const getUserType = createSelector(getAppstateState, (state) =>
  state ? state.navigation?.userType : undefined,
);

const getIsInternalUser = createSelector(getAppstateState, (state) =>
  state ? state.navigation?.isInternalUser : undefined,
);
const getLoggedInUserType = createSelector(
  getAppstateState,
  (state) => state.isInternalUser,
);

const getAlerts = createSelector(getAppstateState, (state) => state.alert);
const getUserAgreement = createSelector(
  getAppstateState,
  (state) => state.userAgreement,
);
const getResizeSideNavToggle = createSelector(
  getAppstateState,
  (state) => state.resizeSideNavToggle,
);
export const getAppRedirect = createSelector(
  getAppstateState,
  (state) => state.appRedirect,
);

const getNavigationMenuListSubmenu = createSelector(
  getAppstateState,
  (state) => state.brNavigation?.navigation?.menus,
);
const getNavigationEntitlements = createSelector(
  getAppstateState,
  (state) => state.brNavigation?.navigation?.entitlements,
);

/**
 * When broker navigation (menus + entitlements) is already in the store from a
 * prior successful call to the navigations API, guards can reuse it instead of
 * requesting the service again (e.g. child routes under the same feature).
 */
export const getCachedBrokerNavigationResponse = createSelector(
  getAppstateState,
  (state): GetBrNavigationsResponse | null => {
    const br = state.brNavigation;
    if (!br?.navigation) {
      return null;
    }
    return { data: br };
  },
);
const getRoles = createSelector(
  getAppstateState,
  (state) => state.brNavigation?.roles,
);

const getNavMenuEntitlements = createSelector(getAppstateState, (state) =>
  state.brNavigation?.navigation?.menus?.reduce(
    (x: string[], y: Menu) =>
      x
        .concat([y.entitlement])
        .concat(
          y.subMenus && y.subMenus.length > 0
            ? y.subMenus.map((z) => z.entitlement)
            : [],
        ),
    [],
  ),
);

const getLegalAffirmStatus = createSelector(
  getAppstateState,
  (state) => state.legalAffirmStatus,
);

const getStatementAccessRequest = createSelector(
  getAppstateState,
  (state) => state.statementAccessRequest,
);

const getTools = createSelector(getAppstateState, (state) => state.tools);

const hasOneOfEntitlements = (props: {
  entitlements: Array<Entitlement | EntitlementLob>;
}) =>
  createSelector(
    getNavigationEntitlements,
    // getUserLob,
    (userEntitlements) =>
      userEntitlements
        ? // eslint-disable-next-line sonarjs/no-identical-functions
          props.entitlements.some((v) => {
            // eslint-disable-next-line no-prototype-builtins
            if (v.hasOwnProperty('entitlement')) {
              return false;
            }
            return userEntitlements.includes(v as Entitlement);
          })
        : false,
  );

const hasOneOfNavMenuEntitlements = (props: {
  entitlements: Array<Entitlement | EntitlementLob>;
}) =>
  createSelector(
    getNavMenuEntitlements,
    // getUserLob,
    (navMenuEntitlements) =>
      navMenuEntitlements
        ? // eslint-disable-next-line sonarjs/no-identical-functions
          props.entitlements.some((v) => {
            // eslint-disable-next-line no-prototype-builtins
            if (v.hasOwnProperty('entitlement')) {
              return false;
            }
            return navMenuEntitlements.includes(v as Entitlement);
          })
        : false,
  );

export const getDetailedProfile = createSelector(
  getProfileState,
  (state) => state.data,
);

export const getWelcomeTileSelector = createSelector(
  getAppstateState,
  (state) =>
    state.welcome?.contents.map((x: WecomeContent) => {
      const res: WecomeContent = {
        ...x,
        ...{ env: state.welcome?.metadata?.environment },
      };
      return res;
    }),
);

export const getBusinessALertData = createSelector(
  getAppstateState,
  (state) => state.businessAlert,
);

export const getLastVisitedUser = createSelector(
  getAppstateState,
  (state) => state.lastVisitedUser,
);

export const getOnboardingFromData = createSelector(
  getAppstateState,
  (state) => state.Onboarding,
);

export const appStateQuery = {
  getCachedBrokerNavigationResponse,
  getDetailsLoaded,
  getDetailsError,
  getAdditionalDetailCode,
  getNavigation,
  getSsoId,
  getSsoIdEnc,
  getFirstName,
  getLastName,
  getIsInternalUser,
  getUserType,
  getResizeSideNavToggle,
  getAppRedirect,
  getAlerts,
  getUserAgreement,
  getNavigationMenuListSubmenu,
  getNavigationEntitlements,
  getRoles,
  getLoggedInUserType,
  getIsBroker,
  hasOneOfEntitlements,
  hasOneOfNavMenuEntitlements,
  getLegalAffirmStatus,
  getDetailedProfile,
  getTools,
  getWelcomeTileSelector,
  getBusinessALertData,
  getStatementAccessRequest,
  getEmail,
  getPhoneNumber,
  getIsNpnPilot,
  getNavMenuEntitlements,
  getlobBeforeLogin,
  getLastVisitedUser,
  partyRole,
  agencyName,
  getOnboardingFromData,
};
